import api from "./api";
import {
  CrmRow,
  FormatError,
  FormatErrorCode,
  FORMAT_ERROR_FIELD,
} from "../utils/subscriberSyncUpload/compareRow";
import {
  UploadRow,
  FinalizeSummary,
  NoteOutcome,
} from "../context/subscriberSyncUpload/reducer";

// Mirrors api/app/Models/SubscriberSyncUpload.SubscriberSyncUploadRowStatus
const ROW_STATUS_SUCCESS = 1;
const ROW_STATUS_FAILED = 2;

export type CreateDraftParams = {
  level1ResourceId: string;
  rows: CrmRow[];
  // Source file name, surfaced in the upload history list's File column (mirrors bulk meter upload).
  fileName?: string;
};

export type CreateDraftResult = {
  uploadId: string;
  rows: UploadRow[];
};

const toRowData = (row: CrmRow) => {
  const data: Record<string, unknown> = {
    accountNumber: row.accountNumber.trim(),
  };
  const optional: Array<[keyof CrmRow, string]> = [
    ["customerName", "name"],
    ["abn", "abn"],
    ["acn", "acn"],
    ["email", "email"],
    ["mobile", "mobile"],
    ["street", "street"],
    ["town", "town"],
    ["region", "region"],
    ["postcode", "postcode"],
    ["country", "country"],
  ];
  for (const [crmKey, backendKey] of optional) {
    const value = (row[crmKey] ?? "").toString().trim();
    if (value !== "") data[backendKey] = value;
  }
  // Case-insensitive so common CRM variants (YES / yes / TRUE / n) round-trip instead of being
  // silently dropped and letting the row misread as in_sync. Unrecognised non-blank values are
  // left unset for now (backend validator is boolean-only); flagging them invalid is a follow-up.
  const isActive = (row.isActive ?? "").toString().trim().toLowerCase();
  if (
    isActive === "yes" ||
    isActive === "true" ||
    isActive === "y" ||
    isActive === "1"
  )
    data.isActive = true;
  else if (
    isActive === "no" ||
    isActive === "false" ||
    isActive === "n" ||
    isActive === "0"
  )
    data.isActive = false;
  const note = (row.note ?? "").toString().trim();
  if (note !== "") data.note = note;
  return data;
};

// The backend serialises each row as a Lucid model: the proposed CRM values live
// inside `rowData` (`name`, `accountNumber`, `note`, …), the classification under
// `comparisonStatus`, and the field differences under `diff`. The wizard's
// UploadRow, however, reads `accountNumber`/`customerName`/`rowStatus`/`diffs` at
// the top level (see Review + rowDisplay). Map every UI-facing field here — the
// single choke point for createDraft / getDraft / reclassify / note-edit rows —
// so the Review table renders and filters correctly. (Without the rowStatus map
// every row falls outside ACTIONABLE_STATUSES and the table shows 0 records.)
const toUploadRow = (serverRow: any): UploadRow => {
  const rowData = (serverRow?.rowData ?? {}) as Record<string, unknown>;
  return {
    ...serverRow,
    rowIndex: serverRow?.rowIndex ?? 0,
    accountNumber:
      (rowData.accountNumber as string) ?? serverRow?.accountNumber ?? "",
    customerName: (rowData.name as string) ?? serverRow?.customerName ?? "",
    rowStatus: serverRow?.comparisonStatus ?? serverRow?.rowStatus,
    diffs: serverRow?.diff ?? serverRow?.diffs ?? [],
    // Invalid rows carry their reasons on the backend as a ";"-joined list of machine codes in
    // failureReasonDetail (e.g. "invalid_abn_length;invalid_mobile") — mirroring bulk meter upload.
    // Split into FormatErrors here; the render surfaces translate each code via react-i18next and
    // interpolate the offending value from rowData[field]. Without this the Review table + detail
    // modal showed "Invalid" with no reason, so the operator couldn't tell which field to fix.
    formatErrors:
      serverRow?.formatErrors ??
      (serverRow?.comparisonStatus === "invalid" &&
      serverRow?.failureReasonDetail
        ? String(serverRow.failureReasonDetail)
            .split(";")
            .map((code: string) => code.trim())
            .filter(
              (code: string): code is FormatErrorCode =>
                code in FORMAT_ERROR_FIELD,
            )
            .map(
              (code: FormatErrorCode): FormatError => ({
                code,
                field: FORMAT_ERROR_FIELD[code],
              }),
            )
        : []),
    decision: serverRow?.decision ?? "pending",
    note: (rowData.note as string) ?? "",
    matchedSubscriberId: serverRow?.matchedSubscriberId ?? null,
  };
};

export async function createDraft(
  params: CreateDraftParams,
): Promise<CreateDraftResult> {
  const body = {
    level1ResourceId: params.level1ResourceId,
    ...(params.fileName ? { fileName: params.fileName } : {}),
    rows: params.rows.map(row => ({ rowData: toRowData(row) })),
  };
  const { data } = await api.post("/admin/subscriberSyncUploads", body);
  return {
    uploadId: data.subscriberSyncUpload.id,
    rows: (data.subscriberSyncUpload.rows ?? []).map(toUploadRow),
  };
}

export type RowDecisionInput = "approved" | "declined";

export async function updateRowDecision(params: {
  uploadId: string;
  rowId: string;
  decision: RowDecisionInput;
}): Promise<UploadRow> {
  const { data } = await api.post(
    `/admin/subscriberSyncUploads/${params.uploadId}/rows/${params.rowId}/decision`,
    { decision: params.decision },
  );
  return data.row;
}

// Note-only edit. Persists rowData.note without resetting the row's decision or
// re-classifying it (the row decision endpoint does both). Backend returns 409
// if the row is already ledgered.
export async function updateRowNote(params: {
  uploadId: string;
  rowId: string;
  note: string;
}): Promise<UploadRow> {
  const { data } = await api.patch(
    `/admin/subscriberSyncUploads/${params.uploadId}/rows/${params.rowId}/note`,
    { note: params.note },
  );
  return toUploadRow(data.row);
}

// Remove a single (non-ledgered) row from the draft — mirrors bulk meter upload's trial-run row
// delete. The backend soft-deletes and returns 409 for an already-ledgered row.
export async function deleteRow(params: {
  uploadId: string;
  rowId: string;
}): Promise<void> {
  await api.delete(
    `/admin/subscriberSyncUploads/${params.uploadId}/rows/${params.rowId}`,
  );
}

export async function bulkUpdateRows(params: {
  uploadId: string;
  decision: RowDecisionInput;
  rowIds: string[];
}): Promise<UploadRow[]> {
  const { data } = await api.post(
    `/admin/subscriberSyncUploads/${params.uploadId}/rows/bulkDecision`,
    { decision: params.decision, rowIds: params.rowIds },
  );
  return data.rows;
}

export type FinalizeResult = {
  uploadId: string;
  status: string;
  summary: FinalizeSummary;
  noteOutcome: NoteOutcome;
  // Ids of rows that finalized to a Failed status (STALE drift or other commit failures). These are
  // the rows a re-check (reclassify) can retry without disturbing already-committed or declined rows.
  retryableRowIds: string[];
  // The workflow instance the ledger run was tagged with. Surfaced to the Summary step so the
  // post-finalize panel can show the workflow's audit-trail events (mirrors bulk meter upload's
  // WorkflowComplete pattern, which references the same workflow-instance id).
  workflowInstanceId: string | null;
};

// The finalize response carries a top-level noteOutcome tallying the per-row
// notes the server created/skipped/failed during commit. Per-field `?? 0` yields
// zeros if the object is absent, so callers can read it unconditionally.
const toNoteOutcome = (raw: any): NoteOutcome => ({
  created: Number(raw?.created ?? 0),
  skipped: Number(raw?.skipped ?? 0),
  failed: Number(raw?.failed ?? 0),
});

// Backend returns { subscriberSyncUpload, workflowInstance, rows } — no summary block. Derive
// the counts here so the frontend's FinalizeSummary shape stays stable.
const summarise = (rows: any[]): FinalizeSummary => {
  const summary: FinalizeSummary = {
    approved: 0,
    declined: 0,
    notFound: 0,
    invalid: 0,
    inSync: 0,
    skippedStale: 0,
    failed: 0,
  };
  for (const row of rows) {
    if (
      row.status === ROW_STATUS_SUCCESS &&
      row.decision === "approved" &&
      row.comparisonStatus === "has_differences"
    ) {
      summary.approved += 1;
    } else if (row.decision === "declined") {
      summary.declined += 1;
    } else if (row.comparisonStatus === "not_found") {
      summary.notFound += 1;
    } else if (row.comparisonStatus === "invalid") {
      summary.invalid += 1;
    } else if (row.comparisonStatus === "in_sync") {
      summary.inSync += 1;
    } else if (
      row.status === ROW_STATUS_FAILED &&
      row.failureReasonCode === "STALE"
    ) {
      summary.skippedStale += 1;
    } else if (row.status === ROW_STATUS_FAILED) {
      summary.failed += 1;
    }
  }
  return summary;
};

export async function finalize(params: {
  uploadId: string;
  note?: string;
  isReportable?: boolean;
}): Promise<FinalizeResult> {
  // FR-6: an optional note on the synchronisation record as a whole, entered on the upload step.
  // Only send it (and its reportable flag) when the operator actually wrote something.
  const body = params.note?.trim()
    ? { note: params.note.trim(), isReportable: Boolean(params.isReportable) }
    : undefined;
  const { data } = await api.post(
    `/admin/subscriberSyncUploads/${params.uploadId}/ledger`,
    body,
  );
  const rows = data.rows ?? [];
  const retryableRowIds = rows
    .filter((row: any) => row.status === ROW_STATUS_FAILED)
    .map((row: any) => row.id);
  return {
    uploadId: data.subscriberSyncUpload.id,
    status: String(data.subscriberSyncUpload.status),
    summary: summarise(rows),
    noteOutcome: toNoteOutcome(data.noteOutcome),
    retryableRowIds,
    workflowInstanceId: data.workflowInstance?.id ?? null,
  };
}

export type ReclassifyResult = {
  uploadId: string;
  rows: UploadRow[];
};

// Re-runs classification against current live data, rebuilding each row's snapshot/diff and resetting
// it to pending. Pass `rowIds` to retry only specific rows (e.g. the ones that finalized STALE) so
// prior declines on other rows are left intact; omit it to re-check the whole upload. Returns only the
// rows still in play — committed (Success) rows are filtered out — so the Review step shows just what
// still needs a decision.
export async function reclassify(params: {
  uploadId: string;
  rowIds?: string[];
}): Promise<ReclassifyResult> {
  const { data } = await api.post(
    `/admin/subscriberSyncUploads/${params.uploadId}/reclassify`,
    params.rowIds && params.rowIds.length > 0 ? { rowIds: params.rowIds } : {},
  );
  const rows = (data.subscriberSyncUpload.rows ?? [])
    .filter((row: any) => row.status !== ROW_STATUS_SUCCESS)
    .map(toUploadRow);
  return {
    uploadId: data.subscriberSyncUpload.id,
    rows,
  };
}

export type DraftDetail = {
  uploadId: string;
  level1ResourceId: string;
  status: string;
  rows: UploadRow[];
};

export async function getDraft(uploadId: string): Promise<DraftDetail> {
  const { data } = await api.get(`/admin/subscriberSyncUploads/${uploadId}`);
  return {
    uploadId: data.subscriberSyncUpload.id,
    level1ResourceId: data.subscriberSyncUpload.level1ResourceId,
    status: String(data.subscriberSyncUpload.status),
    rows: (data.subscriberSyncUpload.rows ?? []).map(toUploadRow),
  };
}

export async function cancelDraft(uploadId: string): Promise<void> {
  await api.delete(`/admin/subscriberSyncUploads/${uploadId}`);
}

export async function downloadResult(uploadId: string): Promise<Blob> {
  const { data } = await api.get(
    `/admin/subscriberSyncUploads/${uploadId}/result`,
    {
      responseType: "blob",
    },
  );
  return data;
}

export async function exportSubscribers(params: {
  level1ResourceId: string;
  level0ResourceId?: string;
  isActive?: boolean;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
}): Promise<Blob> {
  const { data } = await api.get("/admin/subscribers/export", {
    params,
    responseType: "blob",
  });
  return data;
}

export type UploadHistoryItem = {
  id: string;
  level1ResourceId: string;
  fileName: string | null;
  status: number;
  createdAt: string;
  updatedAt: string;
  uploadedByUser?: {
    id: string;
    name?: string | null;
    email?: string | null;
  } | null;
  rows?: Array<{ id: string; status: number }>;
};

export async function getSubscriberSyncUploadsByLevel1WRS(
  level1ResourceId: string,
): Promise<UploadHistoryItem[]> {
  const { data } = await api.get("/admin/subscriberSyncUploads", {
    params: { level1ResourceId },
  });
  return data.subscriberSyncUploads ?? [];
}

export async function getAllSubscriberSyncUploads(): Promise<
  UploadHistoryItem[]
> {
  const { data } = await api.get("/admin/subscriberSyncUploads");
  return data.subscriberSyncUploads ?? [];
}
